import { memo, useCallback, useState } from 'react'

import useDispatch from 'hooks/useDispatch'
import { useMapResultsQuery } from '../../hooks/useQueries'

import Map, { MapCityMarker } from '../../components/maps/Map'
import { centerFromBounds } from '../../components/maps/Map/mapUtils'
import { MapMode } from '../../components/layout/ItemGridWithMap/useWhichMapMode'

import { updateFilters } from '../../actions/searchActions'

import useVisitorOrigin from '../useVisitorOrigin'

import MarkerCallout from './MarkerCallout'
import NoResultsMessage from './NoResultsMessage'
import shouldShowMarketCities from './shouldShowMarketCities'
import useMarketCities from '../useMarketCities'

type SearchViewMapProps = {
  filters: GQL_SearchFilters
  searchQuery: string
  mapMode: MapMode
}

// Zoomed out past here, one market fills less than half the map and its
// individual listing pins read as noise
const MARKET_CITIES_MAX_ZOOM = 6

const SearchMap = memo((props: SearchViewMapProps) => {
  const { filters, searchQuery, mapMode } = props
  const dispatch = useDispatch()

  const { type, ne, sw } = filters

  const center = centerFromBounds({ ne, sw })

  const { data, loading } = useMapResultsQuery({
    variables: { params: filters },
  })

  // The dot only appears once the visitor explicitly shares their location —
  // an IP-derived guess on the map reads as surveillance and is often wrong
  const origin = useVisitorOrigin()
  const currentLocation =
    origin?.source === 'explicit' ? origin.coordinates : null

  const results = data?.search?.mapResults ?? []

  // A map zoomed out past any one market swaps listing pins for the market
  // pins; picking one scopes the search back into that market's state
  // Only the derived answer is held in state, so panning within a zoom
  // level re-renders nothing
  const [zoomedOutPastMarkets, setZoomedOutPastMarkets] = useState(false)
  const handleViewportChange = useCallback(({ zoom }: { zoom: number }) => {
    setZoomedOutPastMarkets(zoom <= MARKET_CITIES_MAX_ZOOM)
  }, [])

  const showMarketCities = shouldShowMarketCities({
    filters,
    zoomedOutPastMarkets,
    resultsCount: results.length,
    loading,
  })
  const marketCities = useMarketCities(!showMarketCities)

  const handleCityMarkerClick = (city: MapCityMarker) => {
    dispatch(
      updateFilters({ country: city.countryCode, state: city.stateCode }),
    )
  }

  const showNoResultsMessage =
    !loading &&
    mapMode === 'toggle' &&
    results.length === 0 &&
    !showMarketCities

  return (
    <>
      <Map
        filters={filters}
        results={showMarketCities ? [] : results}
        cityMarkers={showMarketCities ? marketCities : null}
        onCityMarkerClick={handleCityMarkerClick}
        onViewportChange={handleViewportChange}
        loading={loading}
        searchQuery={searchQuery}
        analyticsSource="search"
        PopupComponent={MarkerCallout}
        defaultCenter={center}
        currentLocation={currentLocation}
      />

      {showNoResultsMessage && <NoResultsMessage searchType={type} />}
    </>
  )
})

export default SearchMap
